import { Select, SubText, Text } from '@procurify/ui'
import { useSelector } from 'react-redux'
import {
  type SingleValueProps,
  type OptionProps,
  components as ReactSelectComponents,
} from 'react-select'

import { makeSelectUser } from '@webapp/core/stores/global'
import { useProcurifyIntl } from '@webapp/hooks'
import { type NextApproverChoice } from '@webapp/modules/expense/entities'
import messages from './messages'

export interface IApproverChoiceSelectProps {
  choices: NextApproverChoice[]
  selectedApproverId: Nullable<number>
  onChange: (approverId: Nullable<number>) => void
}

const ApproverOptionLabel = ({
  approver,
  userId,
}: {
  approver: NextApproverChoice
  userId: number
}) => {
  const intl = useProcurifyIntl()

  return (
    <>
      <Text autoMargin={false}>
        {approver.displayLabel}
        {approver.id === userId
          ? ` (${intl.formatMessage(messages.selfApprovalLabel)})`
          : ''}
      </Text>
      {!!approver.approvalDelegatee && (
        <SubText color='textSecondary'>
          {intl.formatMessage(messages.delegatedToLabel, {
            user: approver.approvalDelegatee.fullName,
          })}
          {approver.approvalDelegatee.id === userId
            ? ` (${intl.formatMessage(messages.selfApprovalLabel)})`
            : ''}
        </SubText>
      )}
    </>
  )
}

const SingleValue = (
  props: SingleValueProps<NextApproverChoice, false> & { userId: number }
) => {
  const { userId, ...otherProps } = props
  return (
    <ReactSelectComponents.SingleValue
      {...otherProps}
      className='approval-chain-approver-single-value'
    >
      <ApproverOptionLabel approver={props.data} userId={userId} />
    </ReactSelectComponents.SingleValue>
  )
}

const Option = (
  props: OptionProps<NextApproverChoice, false> & { userId: number }
) => {
  const { userId, ...otherProps } = props
  return (
    <ReactSelectComponents.Option {...otherProps}>
      <ApproverOptionLabel approver={props.data} userId={userId} />
    </ReactSelectComponents.Option>
  )
}

export const ApproverChoiceSelect = ({
  choices,
  selectedApproverId,
  onChange,
}: IApproverChoiceSelectProps) => {
  const intl = useProcurifyIntl()
  const userId = useSelector(makeSelectUser()).id

  const selectedOption =
    choices.find((choice) => choice.id === selectedApproverId) ?? null

  return (
    <Select<NextApproverChoice>
      options={choices}
      components={{
        SingleValue: (componentProps) => (
          <SingleValue {...componentProps} userId={userId} />
        ),
        Option: (componentProps) => (
          <Option {...componentProps} userId={userId} />
        ),
      }}
      placeholder={intl.formatMessage(messages.placeholderText)}
      value={selectedOption}
      getOptionLabel={(opt) => opt.displayLabel}
      getOptionValue={(opt) => opt.id.toString()}
      onChange={(option) => {
        onChange(option ? option.id : null)
      }}
    />
  )
}
